import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Button, Popover, Typography, styled } from "@mui/material";
import { LocalizationProvider, StaticDatePicker } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { Link } from "react-router-dom";
import { IconGoToTab } from "../../utils/icons";
import { MetricItem, Segment } from "./helper";

const EASE = "cubic-bezier(.4,0,.2,1)";

export function Num({ value, dur = 700 }: { value: number; dur?: number }) {
    const safe = Number.isFinite(value) ? value : 0;
    const [d, setD] = useState(0);
    useEffect(() => {
        let s = 0;
        const step = Math.ceil(Math.abs(safe) / (dur / 16));
        const dir = safe >= 0 ? 1 : -1;
        const t = setInterval(() => {
            s += step;
            if (s >= Math.abs(safe)) {
                s = Math.abs(safe);
                clearInterval(t);
            }
            setD(s * dir);
        }, 16);
        return () => clearInterval(t);
    }, [safe, dur]);
    return <>{d.toLocaleString()}</>;
}

const CENTER_TOTAL = "text-[24px] font-extrabold text-text-1 leading-none tabular-nums";
const CENTER_LABEL = "text-[9px] font-semibold text-text-2 tracking-[1px] mt-0.5";
const CHART_CARD = "bg-surface border border-border-token rounded-[14px] px-6 py-[22px] mb-3.5";
const CHART_CARD_BARE = "bg-surface border border-border-token rounded-[14px] px-6 py-[22px] h-full";
const CARD_LABEL_BOLD = "text-[15px] font-bold text-text-1";
const CARD_SUB_LABEL = "text-[12px] text-text-2";
const BADGE_ALERT =
    "text-[10.5px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap text-down bg-down-bg";

// Spec §06: delta indicators are 12px stroke SVGs, never unicode glyphs.
function ArrowGlyph({ dir }: { dir: "up" | "down" | "flat" }) {
    const d = dir === "up" ? "M12 19V5M5 12l7-7 7 7" : dir === "down" ? "M12 5v14M19 12l-7 7-7-7" : "M5 12h14";
    return (
        <svg
            width={12}
            height={12}
            viewBox="0 0 24 24"
            aria-hidden
            focusable="false"
            className="inline-block align-[-1px] mr-0.5"
        >
            <path d={d} fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function useCursorTooltip() {
    const [hovered, setHovered] = useState<number | null>(null);
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const ref = useRef<HTMLDivElement>(null);
    const onMouseMove = (e: React.MouseEvent) => {
        const rect = ref.current?.getBoundingClientRect();
        if (rect) setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    };
    return { hovered, setHovered, pos, ref, onMouseMove };
}

function SegmentTooltip({
    x,
    y,
    color,
    label,
    value,
    pct,
    suffix = "of total",
}: {
    x: number;
    y: number;
    color: string;
    label: string;
    value: number;
    pct: number;
    suffix?: string;
}) {
    return (
        <div
            className="absolute z-30 pointer-events-none -translate-x-1/2 -translate-y-full"
            style={{ left: x, top: y - 12 }}
        >
            <div className="bg-surface border border-border-token rounded-[12px] px-3.5 py-2.5 shadow-[shadow:var(--sh-2)] whitespace-nowrap">
                <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: color }} />
                    <span className="text-[12.5px] font-bold text-text-1 max-w-[200px] overflow-hidden text-ellipsis">{label}</span>
                </div>
                <div className="flex items-baseline gap-1.5 mt-1 pl-[18px]">
                    <span className="text-[14px] font-extrabold text-text-1 leading-none tabular-nums">{value.toLocaleString()}</span>
                    <span className="text-[11px] font-medium text-text-2">
                        {pct}% {suffix}
                    </span>
                </div>
            </div>
        </div>
    );
}

export function Donut({
    segments,
    total,
    size = 160,
    displayValue,
    displayLabel = "TOTAL",
}: {
    segments: Segment[];
    total: number;
    size?: number;
    displayValue?: number;
    displayLabel?: string;
}) {
    const r = 58;
    const cx = size / 2;
    const cy = size / 2;
    const c = 2 * Math.PI * r;
    let offset = 0;
    const centerValue = displayValue ?? total;
    const { hovered, setHovered, pos, ref, onMouseMove } = useCursorTooltip();
    const hoveredSeg = hovered !== null ? segments[hovered] : null;

    return (
        <div
            ref={ref}
            className="relative flex-shrink-0"
            style={{ width: size, height: size }}
            onMouseMove={onMouseMove}
            onMouseLeave={() => setHovered(null)}
        >
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                {/* var() is not valid in SVG presentation attributes — style works */}
                <circle cx={cx} cy={cy} r={r} fill="none" strokeWidth="18" style={{ stroke: "var(--track)" }} />
                {segments.map((seg, i) => {
                    const pct = seg.val / total;
                    const dash = c * pct;
                    const gap = c - dash;
                    const thisOffset = offset;
                    offset += pct * 360;
                    const isHovered = hovered === i;
                    return (
                        <circle
                            key={i}
                            cx={cx}
                            cy={cy}
                            r={r}
                            fill="none"
                            stroke={seg.color}
                            strokeWidth={isHovered ? 22 : 18}
                            strokeDasharray={`${dash} ${gap}`}
                            onMouseEnter={() => setHovered(i)}
                            style={{
                                transform: `rotate(${thisOffset - 90}deg)`,
                                transformOrigin: "center",
                                transition: "stroke-dasharray 0.8s ease, stroke-width 0.15s ease, opacity 0.15s ease",
                                opacity: hovered === null || isHovered ? 1 : 0.35,
                                cursor: "pointer",
                            }}
                        />
                    );
                })}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className={CENTER_TOTAL}>
                    <Num value={centerValue} />
                </div>
                <div className={CENTER_LABEL}>{displayLabel}</div>
            </div>
            {hoveredSeg && (
                <SegmentTooltip
                    x={pos.x}
                    y={pos.y}
                    color={hoveredSeg.color}
                    label={hoveredSeg.label}
                    value={hoveredSeg.val}
                    pct={Math.round((hoveredSeg.val / total) * 100)}
                />
            )}
        </div>
    );
}

export function HalfDonut({ segments, total, size = 260 }: { segments: Segment[]; total: number; size?: number }) {
    const r = (size - 28) / 2;
    const strokeW = 22;
    const cx = size / 2;
    const cy = r + strokeW / 2;
    const halfLength = Math.PI * r;
    const height = cy + strokeW / 2 + 8;
    const pathD = `M ${cx - r},${cy} A ${r},${r} 0 0 1 ${cx + r},${cy}`;
    let offset = 0;
    const { hovered, setHovered, pos, ref, onMouseMove } = useCursorTooltip();
    const hoveredSeg = hovered !== null ? segments[hovered] : null;

    return (
        <div
            ref={ref}
            className="relative flex-shrink-0"
            style={{ width: size, height }}
            onMouseMove={onMouseMove}
            onMouseLeave={() => setHovered(null)}
        >
            <svg width={size} height={height} viewBox={`0 0 ${size} ${height}`}>
                <path d={pathD} fill="none" strokeWidth={strokeW} strokeLinecap="round" style={{ stroke: "var(--track)" }} />
                {segments.map((seg, i) => {
                    const pct = seg.val / total;
                    const dash = pct * halfLength;
                    const startOffset = -offset;
                    offset += dash;
                    const isHovered = hovered === i;
                    return (
                        <path
                            key={i}
                            d={pathD}
                            fill="none"
                            stroke={seg.color}
                            strokeWidth={isHovered ? strokeW + 4 : strokeW}
                            strokeDasharray={`${Math.max(dash - 1, 0)} ${halfLength}`}
                            strokeDashoffset={startOffset}
                            onMouseEnter={() => setHovered(i)}
                            style={{
                                transition: "stroke-dasharray 0.8s ease, stroke-width 0.15s ease, opacity 0.15s ease",
                                opacity: hovered === null || isHovered ? 1 : 0.35,
                                cursor: "pointer",
                            }}
                        />
                    );
                })}
            </svg>
            <div className="absolute left-0 right-0 flex flex-col items-center" style={{ top: cy - 22 }}>
                <div className={CENTER_TOTAL}>
                    <Num value={total} />
                </div>
                <div className={CENTER_LABEL}>TOTAL</div>
            </div>
            {hoveredSeg && (
                <SegmentTooltip
                    x={pos.x}
                    y={pos.y}
                    color={hoveredSeg.color}
                    label={hoveredSeg.label}
                    value={hoveredSeg.val}
                    pct={Math.round((hoveredSeg.val / total) * 100)}
                />
            )}
        </div>
    );
}

function LegendRows({ segments, total }: { segments: Segment[]; total: number }) {
    return (
        <div className="flex flex-col gap-1.5 flex-1 min-w-[180px] max-h-[220px] overflow-y-auto pr-2">
            {segments.map((s, i) => (
                <div key={i} className="flex items-center gap-3 text-[12.5px]">
                    <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: s.color }} />
                    <span className="flex-1 text-text-2 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{s.label}</span>
                    <span className="font-semibold text-text-2 min-w-[48px] text-center flex-shrink-0">
                        {Math.round((s.val / total) * 100)}%
                    </span>
                    <span className="font-bold text-text-1 min-w-[56px] text-center flex-shrink-0 ml-[18px] tabular-nums">
                        {s.val.toLocaleString()}
                    </span>
                </div>
            ))}
        </div>
    );
}

export function DonutSection({
    title,
    desc,
    segments,
    total,
    displayValue,
    displayLabel,
    bare = false,
}: {
    title: string;
    desc: string;
    segments: Segment[];
    total: number;
    displayValue?: number;
    displayLabel?: string;
    bare?: boolean;
}) {
    return (
        <div className={bare ? CHART_CARD_BARE : CHART_CARD}>
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <span className={CARD_LABEL_BOLD}>{title}</span>
                <span className={CARD_SUB_LABEL}>{desc}</span>
            </div>
            <div className="flex items-center gap-6 flex-wrap">
                <Donut segments={segments} total={total} displayValue={displayValue} displayLabel={displayLabel} />
                <LegendRows segments={segments} total={total} />
            </div>
        </div>
    );
}

export function HalfDonutSection({
    title,
    desc,
    segments,
    total,
}: {
    title: string;
    desc: string;
    segments: Segment[];
    total: number;
}) {
    return (
        <div className={`${CHART_CARD} mb-8`}>
            <div className="flex items-center justify-between mb-7 gap-2 flex-wrap">
                <span className={CARD_LABEL_BOLD}>{title}</span>
                <span className={CARD_SUB_LABEL}>{desc}</span>
            </div>
            <div className="flex items-center gap-6 flex-wrap">
                <HalfDonut segments={segments} total={total} />
                <LegendRows segments={segments} total={total} />
            </div>
        </div>
    );
}

export function SectionHeader({
    title,
    desc,
    color,
    viewAllUrl,
}: {
    title: string;
    desc?: string;
    color: string;
    viewAllUrl?: string;
}) {
    return (
        <div className="flex items-center justify-between pt-8 pb-4">
            <div className="flex items-center gap-3">
                <div className="w-1 h-7 rounded" style={{ background: color }} />
                <div>
                    <div className="text-[18px] font-bold text-text-1">{title}</div>
                    {desc && <div className="text-[12.5px] text-text-2 mt-px">{desc}</div>}
                </div>
            </div>
            {viewAllUrl && (
                <Link to={viewAllUrl} className="text-[13px] font-medium text-accent-text no-underline hover:opacity-75">
                    View all →
                </Link>
            )}
        </div>
    );
}

function ChangeChip({ change, val, up }: { change: number; val: number; up?: boolean }) {
    const [rect, setRect] = useState<DOMRect | null>(null);
    const anchorRef = useRef<HTMLSpanElement>(null);
    const isZero = change === 0;
    const isUp = up !== undefined ? up : change > 0;
    const prevVal = val - change;
    const dir: "up" | "down" | "flat" = isZero ? "flat" : isUp ? "up" : "down";
    const text = `${change > 0 ? "+" : ""}${change.toLocaleString()}`;
    return (
        <span
            ref={anchorRef}
            className="relative"
            onMouseEnter={() => setRect(anchorRef.current?.getBoundingClientRect() ?? null)}
            onMouseLeave={() => setRect(null)}
        >
            <span
                className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap cursor-default tabular-nums ${
                    isZero
                        ? "text-text-2 bg-surface-2"
                        : isUp
                          ? "text-up bg-up-bg"
                          : "text-down bg-down-bg"
                }`}
            >
                <ArrowGlyph dir={dir} />
                {text}
            </span>
            {rect &&
                createPortal(
                    <div
                        className="fixed z-[9999] pointer-events-none"
                        style={{ left: rect.right, top: rect.top - 8, transform: "translate(-100%, -100%)" }}
                    >
                        <div className="relative">
                            <div className="bg-surface border border-border-token rounded-[12px] px-3.5 py-2.5 shadow-[shadow:var(--sh-2)] whitespace-nowrap">
                                <div className="flex items-center justify-between gap-6">
                                    <span className="text-[10px] font-semibold uppercase tracking-[0.5px] text-text-2">This Period</span>
                                    <span className="text-[12.5px] font-bold text-text-1 tabular-nums">{val.toLocaleString()}</span>
                                </div>
                                <div className="flex items-center justify-between gap-6 mt-1">
                                    <span className="text-[10px] font-semibold uppercase tracking-[0.5px] text-text-2">Previous</span>
                                    <span className="text-[12.5px] font-bold text-text-1 tabular-nums">{prevVal.toLocaleString()}</span>
                                </div>
                                <div
                                    className={`text-[11px] font-semibold mt-1.5 pt-1.5 border-t border-border-token ${
                                        isZero ? "text-text-2" : isUp ? "text-up" : "text-down"
                                    }`}
                                >
                                    <ArrowGlyph dir={dir} />
                                    {text} vs. previous period
                                </div>
                            </div>
                            <div className="absolute right-4 top-full h-0 w-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-[var(--border)]" />
                            <div className="absolute right-4 top-full -mt-px h-0 w-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-[var(--surface)]" />
                        </div>
                    </div>,
                    document.body,
                )}
        </span>
    );
}

export function MetricCard({
    item,
    index,
    delay = 0,
    loaded,
    onLinkClick,
}: {
    item: MetricItem;
    index: number;
    delay?: number;
    loaded: boolean;
    onLinkClick?: (url: string) => void;
}) {
    const ratio = item.target && item.target > 0 ? (item.val / item.target) * 100 : 0;
    const fillPct = Number.isFinite(ratio) ? Math.max(0, Math.min(ratio, 100)) : 0;
    const hasChange = item.change !== undefined;
    return (
        <div className="relative bg-surface border border-border-token rounded-[14px] px-[22px] py-5 transition-all hover:border-tint hover:shadow-[shadow:var(--sh-2)] min-w-0 overflow-hidden h-full flex flex-col">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <span className="text-[12.5px] font-medium text-text-2">{item.label}</span>
                <div className="flex items-center gap-2">
                    {hasChange && (
                        <ChangeChip change={item.change!} val={item.val} up={item.up} />
                    )}
                    {item.linkUrl && onLinkClick && (
                        <button
                            type="button"
                            title={`View ${item.label}`}
                            onClick={() => onLinkClick(item.linkUrl!)}
                            className="flex items-center p-0 bg-transparent border-0 cursor-pointer shrink-0 hover:opacity-70 transition-opacity text-muted"
                        >
                            <IconGoToTab width={14} height={14} stroke="currentColor" />
                        </button>
                    )}
                </div>
            </div>
            <div className="text-[32px] font-extrabold text-text-1 leading-none tracking-[-0.5px] tabular-nums mb-3">
                {item.display || <Num value={item.val} />}
            </div>
            {item.target && item.color && (
                <div className="h-2 bg-track rounded overflow-hidden mb-2">
                    <div
                        className="h-full rounded min-w-[6px]"
                        style={{
                            width: loaded ? `${fillPct}%` : "0%",
                            background: item.color,
                            transition: `width 1s ${EASE}`,
                            transitionDelay: `${delay + index * 100}ms`,
                        }}
                    />
                </div>
            )}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
                {item.sub && <span className="text-[11.5px] text-text-2">{item.sub}</span>}
                {item.alert && <span className={BADGE_ALERT}>● {item.alert}</span>}
            </div>
        </div>
    );
}

export function MetricSkeleton() {
    return (
        <div className="bg-surface border border-border-token rounded-[14px] px-[22px] py-5 h-full animate-pulse">
            <div className="h-3 w-24 bg-track rounded mb-4" />
            <div className="h-7 w-20 bg-track rounded mb-4" />
            <div className="h-2 w-full bg-track rounded" />
        </div>
    );
}

export interface StageRow {
    label: string;
    val: number;
    color: string;
    group: string;
    terminal?: boolean;
    linkUrl?: string;
}

export const countInFlight = (rows: StageRow[]) =>
    rows.reduce((sum, r) => (r.terminal ? sum : sum + r.val), 0);

export function StageStrip({
    rows,
    loaded,
    onRowClick,
}: {
    rows: StageRow[];
    loaded: boolean;
    onRowClick?: (url: string) => void;
}) {
    const activeTotal = countInFlight(rows);
    const groups: { name: string; rows: StageRow[] }[] = [];
    rows.forEach((r) => {
        const last = groups[groups.length - 1];
        if (last && last.name === r.group) last.rows.push(r);
        else groups.push({ name: r.group, rows: [r] });
    });

    return (
        <div className="flex flex-wrap gap-3 mb-3.5">
            {groups.map((g, gi) => (
                <div
                    key={g.name}
                    className="flex-1 min-w-[240px] bg-surface border border-border-token rounded-[14px] px-3.5 pt-3 pb-3.5"
                >
                    <div className="flex items-center gap-2 mb-2.5 px-1">
                        <span
                            className="h-[3px] w-4 rounded-full flex-shrink-0"
                            style={{ background: g.rows[0].color }}
                        />
                        <span className="text-[10px] font-bold tracking-[0.09em] uppercase text-muted">
                            {g.name}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        {g.rows.map((r, i) => {
                            const clickable = !!(r.linkUrl && onRowClick);
                            const share =
                                r.terminal || activeTotal === 0
                                    ? null
                                    : Math.round((r.val / activeTotal) * 100);
                            const active = r.val > 0;
                            return (
                                <button
                                    key={r.label}
                                    type="button"
                                    disabled={!clickable}
                                    title={clickable ? `View ${r.label} deliverables` : undefined}
                                    onClick={() => clickable && onRowClick!(r.linkUrl!)}
                                    className={`group flex-1 min-w-0 flex flex-col items-start rounded-[11px] border px-3 py-2.5 text-left transition-all ${
                                        clickable ? "cursor-pointer hover:-translate-y-[1px] hover:shadow-[shadow:var(--sh-1)]" : "cursor-default"
                                    }`}
                                    style={{
                                        background: active ? `${r.color}14` : "transparent",
                                        borderColor: active ? `${r.color}40` : "var(--border)",
                                    }}
                                >
                                    <span className="flex items-baseline gap-1.5 w-full">
                                        <span
                                            className={`text-[23px] font-extrabold leading-none tabular-nums ${
                                                active ? "text-text-1" : "text-text-2"
                                            }`}
                                        >
                                            {loaded ? <Num value={r.val} dur={900} /> : 0}
                                        </span>
                                        {active && share !== null && (
                                            <span className="text-[10.5px] font-semibold text-text-2">{share}%</span>
                                        )}
                                        {clickable && (
                                            <span className="ml-auto self-start text-muted opacity-60 group-hover:opacity-100 transition-opacity">
                                                <IconGoToTab width={12} height={12} stroke="currentColor" />
                                            </span>
                                        )}
                                    </span>
                                    <span className="mt-1.5 min-h-[45px] text-[11.5px] font-semibold text-text-2 leading-[1.3]">
                                        {r.label}
                                    </span>
                                    <span
                                        className={`mt-2 h-[3px] w-full rounded-full overflow-hidden ${
                                            share === null ? "" : "bg-track"
                                        }`}
                                    >
                                        {share !== null && (
                                            <span
                                                className="block h-full rounded-full"
                                                style={{
                                                    width: loaded ? `${share}%` : "0%",
                                                    background: r.color,
                                                    transition: `width 1s ${EASE}`,
                                                    transitionDelay: `${(gi * 2 + i) * 70 + 600}ms`,
                                                }}
                                            />
                                        )}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}

export function PipelineList({
    rows,
    loaded,
    onRowClick,
}: {
    rows: { label: string; val: number; barPct: number; color: string; tc: string; alert?: string | null; linkUrl?: string }[];
    loaded: boolean;
    onRowClick?: (url: string) => void;
}) {
    const { hovered, setHovered, pos, ref, onMouseMove } = useCursorTooltip();
    const totalVal = rows.reduce((s, r) => s + r.val, 0);
    const hoveredRow = hovered !== null ? rows[hovered] : null;

    return (
        <div
            ref={ref}
            className="relative bg-surface border border-border-token rounded-[14px] px-6 py-[22px] mb-3.5"
            onMouseMove={onMouseMove}
        >
            <div className="flex flex-col gap-2.5">
                {rows.map((p, i) => {
                    const clickable = !!(p.alert && p.linkUrl && onRowClick);
                    return (
                        <div key={i} className="flex items-center gap-3.5">
                            <div className="w-[180px] flex-shrink-0 text-[12.5px] text-text-2 text-right whitespace-nowrap overflow-hidden text-ellipsis max-[900px]:w-[100px] max-[900px]:text-[11px]">
                                {p.label}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div
                                    className="h-7 bg-track rounded-md overflow-hidden relative cursor-default"
                                    onMouseEnter={() => setHovered(i)}
                                    onMouseLeave={() => setHovered(null)}
                                >
                                    <div
                                        className="h-full rounded-md min-w-[28px] flex items-center justify-center"
                                        style={{
                                            width: loaded ? `${p.barPct}%` : "0%",
                                            background: p.color,
                                            transition: `width 1s ${EASE}`,
                                            transitionDelay: `${i * 80 + 600}ms`,
                                        }}
                                    >
                                        <span className="text-[12px] font-bold leading-none" style={{ color: p.tc }}>
                                            {p.val}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="w-[150px] flex-shrink-0 flex items-center justify-end gap-1.5">
                                {p.alert && <span className={`${BADGE_ALERT} shrink-0`}>● {p.alert}</span>}
                                {clickable && (
                                    <button
                                        type="button"
                                        title={`View ${p.label} projects`}
                                        onClick={() => onRowClick!(p.linkUrl!)}
                                        className="flex shrink-0 p-0 bg-transparent border-0 cursor-pointer hover:opacity-70 transition-opacity text-muted"
                                    >
                                        <IconGoToTab width={14} height={14} stroke="currentColor" />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {hoveredRow && (
                <SegmentTooltip
                    x={pos.x}
                    y={pos.y}
                    color={hoveredRow.color}
                    label={hoveredRow.label}
                    value={hoveredRow.val}
                    pct={totalVal > 0 ? Math.round((hoveredRow.val / totalVal) * 100) : 0}
                    suffix="of pipeline"
                />
            )}
        </div>
    );
}

const StyledRangeDatePicker = styled(StaticDatePicker)({
    border: "1px solid var(--border)",
    borderRadius: "16px 16px 0px 0px",
    width: "100%",
    minWidth: 280,
    "& .MuiPickersToolbar-root": { display: "none" },
    "& .MuiDateCalendar-root": { width: 310 },
});

function RangeColumn({
    title,
    value,
    minDate,
    maxDate,
    onSelect,
}: {
    title: string;
    value: Dayjs | null;
    minDate?: Dayjs;
    maxDate?: Dayjs;
    onSelect: (d: Dayjs) => void;
}) {
    return (
        <Box className="flex flex-col gap-3 w-full p-6 items-center" sx={{ borderRight: { md: "1px solid var(--border)" } }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "var(--text-2)", textTransform: "uppercase", alignSelf: "flex-start" }}>
                {title}
            </Typography>
            <Box className="flex flex-col w-full">
                <StyledRangeDatePicker
                    value={value || null}
                    minDate={minDate}
                    maxDate={maxDate}
                    // @ts-ignore
                    onChange={(v: Dayjs) => onSelect(v)}
                    slotProps={{ actionBar: { actions: [] } }}
                />
                <Box className="flex items-center justify-center bg-surface-2 rounded-lg rounded-tr-none rounded-tl-none p-3 border border-t-0 border-border-token">
                    <Typography variant="body2">{value ? value.format("MMM DD, YYYY") : "Select a date"}</Typography>
                </Box>
            </Box>
        </Box>
    );
}

export function CustomRangePopover({
    anchor,
    onClose,
    onApply,
}: {
    anchor: HTMLElement | null;
    onClose: () => void;
    onApply: (from: string, to: string) => void;
}) {
    const [fromDate, setFromDate] = useState<Dayjs | null>(null);
    const [toDate, setToDate] = useState<Dayjs | null>(null);
    const today = dayjs();

    return (
        <Popover
            open={!!anchor}
            anchorEl={anchor}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
                paper: { sx: { mt: 1, borderRadius: "12px", boxShadow: "var(--sh-2)" } },
            }}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-us">
                <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
                    <RangeColumn title="From Date" value={fromDate} maxDate={toDate || today} onSelect={setFromDate} />
                    <RangeColumn title="To Date" value={toDate} minDate={fromDate || undefined} maxDate={today} onSelect={setToDate} />
                </Box>
            </LocalizationProvider>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, padding: "0 16px 16px" }}>
                <Button onClick={onClose} variant="text">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={!fromDate || !toDate}
                    onClick={() => {
                        if (fromDate && toDate) {
                            onApply(fromDate.format("YYYY-MM-DD"), toDate.format("YYYY-MM-DD"));
                        }
                    }}
                    sx={{
                        // Inverts with the theme: near-black on light, near-white on dark
                        background: "var(--text)",
                        color: "var(--page)",
                        "&:hover": { background: "var(--text)", opacity: 0.9 },
                    }}
                >
                    Apply
                </Button>
            </Box>
        </Popover>
    );
}
